import type { AddonInfraConfig } from "./addonInfra";
import { parseFeatureDescription } from "./heuristicParser";
import { normalizeInputMode } from "./inputMode";
import { normalizeAwsRegion } from "./awsRegion";
import { mergeChecklist } from "./checklistIds";
import { coerceScenarioSnapshot } from "./scenario";
import { normalizeDeploymentStack } from "./deploymentStack";
import { normalizeSavingsLevers } from "./savingsLevers";
import { normalizeAudienceMode } from "./audienceMode";
import { normalizeLlmPriceProfile } from "./llmPriceProfile";
import { coerceWorkload } from "./workloadState";
import { normalizeComputeProfile } from "./computeProfile";
import { normalizeDisplayCurrency } from "./displayCurrency";
import type { PersistedUi } from "./persistedUi";
import {
  normalizeCostDisplayUnit,
  normalizeExplorerByService,
  normalizeScenarioNotes,
} from "./persistedUi";
import { MODEL_VERSION, SCHEMA_VERSION } from "./modelMeta";
import type { CostPerformanceMode } from "./types";

export const SCENARIO_BUNDLE_VERSION = 1 as const;
const HASH_PREFIX = "dm=1.";

const MAX_JSON_CHARS = 120_000;
const MAX_INPUT_CHARS = 500_000;

function normalizeCpMode(x: unknown): CostPerformanceMode | undefined {
  if (x === "cheaper" || x === "balanced" || x === "faster") return x;
  return undefined;
}

function utf8ToBase64Url(json: string): string {
  const bytes = new TextEncoder().encode(json);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  const b64 = btoa(binary);
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToUtf8(b64url: string): string {
  const padded = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4;
  const b64 = pad ? padded + "=".repeat(4 - pad) : padded;
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function unwrapBundle(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const app = o.app;
  if (typeof app === "string" && app !== "dmeterline") return null;
  const v = o.v;
  if (v !== undefined && v !== SCENARIO_BUNDLE_VERSION) return null;
  return o;
}

/** Відновлює повний знімок для localStorage / стану застосунку. */
export function normalizeImportedPersisted(
  raw: unknown,
  fallbackInput: string,
): PersistedUi | null {
  const o = unwrapBundle(raw);
  if (!o) return null;

  const inputRaw = o.input;
  const input =
    typeof inputRaw === "string"
      ? inputRaw.slice(0, MAX_INPUT_CHARS)
      : fallbackInput.slice(0, MAX_INPUT_CHARS);

  let workload = coerceWorkload(o.workload);
  if (!workload) {
    try {
      workload = parseFeatureDescription(input);
    } catch {
      return null;
    }
  }

  const userMult = clampMult(o.userMult, 1);
  const reqMult = clampMult(o.reqMult, 1);
  const cpMode = normalizeCpMode(o.cpMode) ?? "balanced";
  const awsRegion = normalizeAwsRegion(o.awsRegion);

  let budgetCapUsd: number | null = null;
  const bRaw = o.budgetCapUsd;
  if (bRaw !== undefined && bRaw !== null) {
    const n = Number(bRaw);
    if (Number.isFinite(n) && n > 0) budgetCapUsd = n;
  }

  const checklist = mergeChecklist(o.checklist as Record<string, boolean> | undefined);
  const scenarioA = coerceScenarioSnapshot(o.scenarioA);
  const scenarioB = coerceScenarioSnapshot(o.scenarioB);
  const savings = normalizeSavingsLevers(o.savings);
  const deploymentStack = normalizeDeploymentStack(
    o.deploymentStack,
    o.addonInfra as AddonInfraConfig | undefined,
  );

  const abs = o.actualBillStr;
  const actualBillStr = typeof abs === "string" ? abs.slice(0, 256) : undefined;

  return {
    inputMode: normalizeInputMode(o.inputMode),
    input,
    workload,
    userMult,
    reqMult,
    cpMode,
    awsRegion,
    budgetCapUsd,
    checklist,
    scenarioA: scenarioA ?? undefined,
    scenarioB: scenarioB ?? undefined,
    savings,
    deploymentStack,
    llmPriceProfile: normalizeLlmPriceProfile(o.llmPriceProfile),
    actualBillStr,
    costDisplayUnit: normalizeCostDisplayUnit(o.costDisplayUnit),
    audienceMode: normalizeAudienceMode(o.audienceMode),
    scenarioNotes: normalizeScenarioNotes(o.scenarioNotes),
    displayCurrency: normalizeDisplayCurrency(o.displayCurrency),
    computeProfile: normalizeComputeProfile(o.computeProfile),
    explorerByService: normalizeExplorerByService(o.explorerByService),
  };
}

function clampMult(x: unknown, def: number): number {
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return def;
  return Math.min(1000, Math.max(0.01, n));
}

export function buildScenarioExportObject(state: PersistedUi): Record<string, unknown> {
  return {
    v: SCENARIO_BUNDLE_VERSION,
    app: "dmeterline",
    modelVersion: MODEL_VERSION,
    schemaVersion: SCHEMA_VERSION,
    inputMode: state.inputMode,
    input: state.input,
    workload: state.workload,
    userMult: state.userMult,
    reqMult: state.reqMult,
    cpMode: state.cpMode,
    awsRegion: state.awsRegion,
    budgetCapUsd: state.budgetCapUsd,
    checklist: state.checklist,
    scenarioA: state.scenarioA,
    scenarioB: state.scenarioB,
    savings: state.savings,
    deploymentStack: state.deploymentStack,
    llmPriceProfile: state.llmPriceProfile,
    actualBillStr: state.actualBillStr,
    costDisplayUnit: state.costDisplayUnit,
    audienceMode: state.audienceMode,
    scenarioNotes: state.scenarioNotes,
    displayCurrency: state.displayCurrency,
    computeProfile: state.computeProfile,
    explorerByService: state.explorerByService,
  };
}

/** Максимальна довжина фрагмента hash (після `dm=1.`), щоб уникнути обрізання URL у браузерах. */
const MAX_HASH_PAYLOAD_CHARS = 6000;

export type EncodeScenarioHashResult =
  | { ok: true; fragment: string }
  | { ok: false; reason: "too_long" };

export function encodeScenarioHashPayload(
  state: PersistedUi,
): EncodeScenarioHashResult {
  const json = JSON.stringify(buildScenarioExportObject(state));
  if (json.length > MAX_JSON_CHARS) return { ok: false, reason: "too_long" };
  const b64 = utf8ToBase64Url(json);
  const fragment = `${HASH_PREFIX}${b64}`;
  if (fragment.length > MAX_HASH_PAYLOAD_CHARS)
    return { ok: false, reason: "too_long" };
  return { ok: true, fragment };
}

export function decodeScenarioHashFragment(fullHash: string): unknown | null {
  const h = fullHash.replace(/^#/, "");
  if (!h.startsWith(HASH_PREFIX)) return null;
  const b64 = h.slice(HASH_PREFIX.length);
  if (!b64) return null;
  try {
    const json = base64UrlToUtf8(b64);
    return JSON.parse(json) as unknown;
  } catch {
    return null;
  }
}

export function parseScenarioJsonText(text: string): unknown | null {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

export function scenarioShareUrl(fragment: string): string {
  if (typeof window === "undefined") return "";
  const { origin, pathname, search } = window.location;
  return `${origin}${pathname}${search}#${fragment}`;
}

/** Шлях до read-only preview (з урахуванням Vite BASE_URL). */
export function scenarioPreviewPath(): string {
  const b = import.meta.env.BASE_URL;
  if (b === "/" || b === "") return "/preview";
  return `${b.replace(/\/?$/, "/")}preview`;
}

export function scenarioPreviewUrl(fragment: string): string {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}${scenarioPreviewPath()}${window.location.search}#${fragment}`;
}
